import { TrainingLaunch } from '@/src/domain/models/TrainingLaunch';
import { useTranslation } from '@/src/i18n';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';

export interface MyLaunchesScreenColors {
  background: string;
  card: string;
  border: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  primary: string;
}

interface LaunchCardProps {
  launch: TrainingLaunch;
  colors: MyLaunchesScreenColors;
}

function LaunchCard({ launch, colors }: LaunchCardProps) {
  const { t } = useTranslation();
  const date = new Date(launch.timestamp);
  const dateStr = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.cardHeader}>
        <View style={styles.dateRow}>
          <MaterialCommunityIcons name="calendar-outline" size={14} color={colors.textSecondary} />
          <Text style={[styles.dateText, { color: colors.textSecondary }]}>{dateStr} · {timeStr}</Text>
        </View>
        {launch.note ? (
          <Text style={[styles.note, { color: colors.textTertiary }]} numberOfLines={1}>{launch.note}</Text>
        ) : null}
      </View>

      <View style={styles.statsGrid}>
        <StatChip
          icon="speedometer"
          label={t('training.rpm')}
          value={Math.round(launch.rpmMax).toString()}
          colors={colors}
        />
        <StatChip
          icon="star-outline"
          label={t('training.score')}
          value={Math.round(launch.launchScore).toString()}
          colors={colors}
        />
        <StatChip
          icon="target"
          label={t('training.consistency')}
          value={`${Math.round(launch.consistency)}%`}
          colors={colors}
        />
        <StatChip
          icon="timer-outline"
          label={t('training.duration')}
          value={t('training.seconds', { s: launch.launchDuration.toFixed(1) })}
          colors={colors}
        />
      </View>
    </View>
  );
}

interface StatChipProps {
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  label: string;
  value: string;
  colors: MyLaunchesScreenColors;
}

function StatChip({ icon, label, value, colors }: StatChipProps) {
  return (
    <View style={styles.chip}>
      <MaterialCommunityIcons name={icon} size={14} color={colors.primary} />
      <Text style={[styles.chipValue, { color: colors.text }]}>{value}</Text>
      <Text style={[styles.chipLabel, { color: colors.textSecondary }]}>{label}</Text>
    </View>
  );
}

export interface MyLaunchesScreenProps {
  launches: TrainingLaunch[];
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  colors: MyLaunchesScreenColors;
}

export function MyLaunchesScreen({
  launches,
  loading,
  refreshing,
  onRefresh,
  colors,
}: MyLaunchesScreenProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator color={colors.primary} size="large" />
      </View>
    );
  }

  if (launches.length === 0) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="rocket-launch-outline" size={48} color={colors.textSecondary} />
        <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
          {t('training.myLaunchesEmpty')}
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      data={launches}
      keyExtractor={(item) => item.id}
      style={{ backgroundColor: colors.background }}
      contentContainerStyle={styles.list}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
      renderItem={({ item }) => <LaunchCard launch={item} colors={colors} />}
    />
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 32,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 15,
    lineHeight: 22,
  },
  list: {
    padding: 16,
    gap: 12,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    gap: 12,
  },
  cardHeader: {
    gap: 4,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  dateText: {
    fontSize: 13,
    fontWeight: '500',
  },
  note: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: '45%',
    flex: 1,
  },
  chipValue: {
    fontSize: 14,
    fontWeight: '700',
  },
  chipLabel: {
    fontSize: 12,
  },
});
